import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Badge, Button, Card, cn, formatCount } from '@wit-support/ui';
import { groupsFeature } from '../groups.js';
import { Check } from 'lucide-react';
import { splitTemplateIssues } from '../issues.js';
import { displayTitle, displayValue, humanizeDisplay, } from '../registry/value-display.js';
import { useAdsHealth, useCountryLists } from '../shared.js';
export const TEMPLATE_KIND_LABEL_RU = {
    test: 'Тест',
    scale: 'Скейл',
    creative_test: 'Тест креативов',
};
/**
 * Значение чипа сводки словами. Перечисления (`OFFSITE_CONVERSIONS`,
 * `PURCHASE`, `LEARN_MORE`, сокращение `BC`) — подписью опции реестра по
 * значению шаблона (или по самому показу, если он совпал со значением опции).
 * Деньги auto-ads подписывает всегда в долларах (`$25`), поэтому при известной
 * валюте кабинета денежный чип перерисовывается здесь — из значения шаблона в
 * minor units этой валюты.
 */
export function summaryValueText(item, template, desc, currency, ctx = {}) {
    // реестр ещё не загружен — показ сервера всё равно словами (JSON не мелькает)
    if (!desc)
        return humanizeDisplay(item.display, undefined, ctx);
    const raw = template.values[item.fieldKey] ?? template.options[item.fieldKey] ?? desc.default;
    if (desc.type === 'country_list') {
        // id списка → «5 стран (5)»: сервер показывает список его id
        const id = typeof raw === 'string' ? raw : item.display;
        return ctx.countryLists?.some((l) => l.id === id)
            ? displayValue(desc, id, ctx)
            : humanizeDisplay(item.display, desc, ctx);
    }
    if (desc.type === 'money') {
        return currency && typeof raw === 'number'
            ? displayValue(desc, raw, { currency })
            : item.display;
    }
    if (desc.type === 'enum' && (desc.options?.length ?? 0) > 0) {
        const opts = desc.options ?? [];
        const byRaw = raw != null ? opts.find((o) => String(o.value) === String(raw)) : undefined;
        const byDisplay = opts.find((o) => String(o.value) === item.display);
        return (byRaw ?? byDisplay)?.label ?? humanizeDisplay(item.display, desc, ctx);
    }
    return humanizeDisplay(item.display, desc, ctx);
}
/**
 * Подпись чипа сводки целиком — всегда с названием поля: «Advantage+
 * аудитория: выкл.», «Кнопка: Подробнее» (голое «нет» непонятно, к чему оно).
 */
export function summaryChipText(item, template, desc, currency, ctx = {}) {
    const label = item.label !== '' ? item.label : (desc?.label ?? item.fieldKey);
    return `${label}: ${summaryValueText(item, template, desc, currency, ctx)}`;
}
/** Сводка шаблона чипами: «CBO · Bid cap $25 · Мир −14 стран · EN US+UK». */
export function TemplateSummary({ template, registry, currency, }) {
    const countryLists = useCountryLists();
    if (template.summary.length === 0)
        return null;
    const ctx = { countryLists: countryLists.data?.items ?? [] };
    const byKey = new Map(registry ? [...registry.fields, ...registry.options].map((d) => [d.key, d]) : []);
    return (_jsx("div", { className: "flex flex-wrap gap-1", children: template.summary.map((s) => {
            const desc = byKey.get(s.fieldKey);
            const label = s.label !== '' ? s.label : (desc?.label ?? s.fieldKey);
            return (_jsx(Badge, { variant: "neutral", className: "max-w-full", "data-testid": `summary-${s.fieldKey}`, title: desc
                    ? displayTitle(desc, template.values[s.fieldKey] ?? template.options[s.fieldKey] ?? desc.default, ctx)
                    : undefined, children: _jsxs("span", { className: "min-w-0", children: [_jsxs("span", { className: "text-[var(--color-text-muted)]", children: [label, ":"] }), ' ', _jsx("span", { className: "[overflow-wrap:anywhere]", children: summaryValueText(s, template, desc, currency, ctx) })] }) }, s.fieldKey));
        }) }));
}
/** Карточки шаблонов: выбор подставляет все значения шаблона в экран запуска. */
export function LaunchTemplateCards({ templates, selectedId, onSelect, registry, currencyOf, }) {
    return (_jsx("div", { className: "grid gap-3 sm:grid-cols-2 xl:grid-cols-3", "data-testid": "template-picker", children: templates.map((t) => {
            const on = t.id === selectedId;
            return (_jsxs(Card, { className: cn('flex flex-col gap-2 p-3', on && 'border-[var(--color-border-focus)] ring-2 ring-[var(--color-border-focus)]'), children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "font-medium", children: t.name }), _jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: [TEMPLATE_KIND_LABEL_RU[t.kind], " \u00B7 \u0432\u0435\u0440\u0441\u0438\u044F ", t.version] })] }), _jsxs(Button, { type: "button", size: "sm", variant: on ? 'primary' : 'secondary', "aria-pressed": on, "aria-label": `Выбрать шаблон «${t.name}»`, onClick: () => onSelect(t), children: [on ? _jsx(Check, { "aria-hidden": true, className: "h-3.5 w-3.5" }) : null, on ? 'Выбран' : 'Выбрать'] })] }), _jsx(TemplateSummary, { template: t, registry: registry ?? null, currency: currencyOf?.(t) ?? null }), _jsx(TemplateGroupsBadges, { template: t }), _jsx(TemplateIssuesBadges, { issues: t.issues })] }, t.id));
        }) }));
}
/**
 * Бейджи замечаний шаблона на карточке: «Ошибки в значениях» — значение не
 * проходит проверку (`invalid`); «Шаблон устарел» — только когда он разошёлся
 * с реестром (неизвестное / устаревшее поле); советы (предупреждения) —
 * нейтральной строкой «Рекомендации».
 */
export function TemplateIssuesBadges({ issues }) {
    const { drift, invalid, advice } = splitTemplateIssues(issues);
    if (drift.length === 0 && invalid.length === 0 && advice.length === 0)
        return null;
    return (_jsxs("div", { className: "flex flex-wrap gap-1", children: [invalid.length > 0 ? (_jsxs(Badge, { variant: "danger", title: invalid.map((i) => i.message).join('\n'), children: ["\u041E\u0448\u0438\u0431\u043A\u0438 \u0432 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F\u0445: ", invalid.length] })) : null, drift.length > 0 ? (_jsxs(Badge, { variant: "warning", title: drift.map((i) => i.message).join('\n'), children: ["\u0428\u0430\u0431\u043B\u043E\u043D \u0443\u0441\u0442\u0430\u0440\u0435\u043B: ", formatCount(drift.length, ['замечание', 'замечания', 'замечаний'])] })) : null, advice.length > 0 ? (_jsxs(Badge, { variant: "info", title: advice.map((i) => i.message).join('\n'), children: ["\u0420\u0435\u043A\u043E\u043C\u0435\u043D\u0434\u0430\u0446\u0438\u0438: ", advice.length] })) : null] }));
}
/**
 * Чип групп шаблона (v4 §11.1): «Структура: 1-2-1 · LAL23, LAL45» при
 * «Кампании на каждый креатив» (форма известна заранее), иначе «Группы
 * шаблона: LAL23, LAL45»; при выключенных группах — бейдж «группы выключены».
 */
export function TemplateGroupsBadges({ template: t, }) {
    const health = useAdsHealth();
    const groups = t.adSetGroups?.groups ?? [];
    if (groups.length === 0)
        return null;
    const labels = groups.map((g) => g.label).join(', ');
    const perItem = (t.options['opt.layout'] ?? 'campaign_per_item') === 'campaign_per_item';
    const off = groupsFeature(health.data, health.isError, health.isPending) === 'off';
    return (_jsxs("div", { className: "flex flex-wrap gap-1", "data-testid": "template-groups-badges", children: [_jsx(Badge, { variant: "outline", className: "[overflow-wrap:anywhere]", children: perItem ? `Структура: 1-${groups.length}-1 · ${labels}` : `Группы шаблона: ${labels}` }), off ? _jsx(Badge, { variant: "warning", children: "\u0433\u0440\u0443\u043F\u043F\u044B \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D\u044B" }) : null] }));
}
//# sourceMappingURL=launch-template-cards.js.map